import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

const API_BASE = "/api/bdb/pet";

/* ============================================================
   IMÁGENES DEL CACHORRO PITBULL
============================================================ */

const dogImages = {
  idle: "https://res.cloudinary.com/dekoxefky/image/upload/v1780849584/dog_idle.png_xgtmbd.png",
  walk1: "https://res.cloudinary.com/dekoxefky/image/upload/v1780849583/dog_walk_1.png_zujooi.png",
  walk2: "https://res.cloudinary.com/dekoxefky/image/upload/v1780849579/dog_walk_2.png_ojfxzd.png",
  walk3: "https://res.cloudinary.com/dekoxefky/image/upload/v1780849580/dog_walk_3.png_qlfqed.png",
  eat: "https://res.cloudinary.com/dekoxefky/image/upload/v1780849567/dog_eat.png_dho0wv.png",
  drink: "https://res.cloudinary.com/dekoxefky/image/upload/v1780849567/dog_drink.png_wmxknb.png",
  play: "https://res.cloudinary.com/dekoxefky/image/upload/v1780849575/dog_play.png_ciyajr.png",
  sleep: "https://res.cloudinary.com/dekoxefky/image/upload/v1780849566/dog_sleep.png_wgpc5i.png",
  weak: "https://res.cloudinary.com/dekoxefky/image/upload/v1780849557/dog_weak.png_pokfxz.png",
  fallen: "https://res.cloudinary.com/dekoxefky/image/upload/v1780849560/dog_fallen.png_vlinpk.png",
};

function getPetImage(pet, animation, walkFrame = 1) {
  if (pet?.status === "fallen") return dogImages.fallen;
  if (pet?.status === "weak") return dogImages.weak;

  if (animation === "eat") return dogImages.eat;
  if (animation === "drink") return dogImages.drink;
  if (animation === "play") return dogImages.play;
  if (animation === "sleep") return dogImages.sleep;

  if (animation === "walk") {
    if (walkFrame === 1) return dogImages.walk1;
    if (walkFrame === 2) return dogImages.walk2;
    return dogImages.walk3;
  }

  return dogImages.idle;
}

/* ============================================================
   API
============================================================ */

function getToken() {
  return sessionStorage.getItem("token");
}

function petApi() {
  const token = getToken();

  return axios.create({
    baseURL: API_BASE,
    headers: {
      token,
    },
  });
}

/* ============================================================
   HELPERS VISUALES
============================================================ */

function fmtNumber(value) {
  return Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 3,
  });
}

function statusColor(status) {
  if (status === "alive") return "#00e676";
  if (status === "weak") return "#ffb300";
  if (status === "fallen") return "#ff1744";
  return "#ffb300";
}

function statusText(status) {
  if (status === "alive") return "Vivo";
  if (status === "weak") return "Débil";
  if (status === "fallen") return "Caído";
  return status || "Desconocido";
}

function stageText(stage) {
  if (stage === "puppy") return "Cachorro";
  if (stage === "young") return "Joven";
  if (stage === "adult") return "Adulto";
  if (stage === "veteran") return "Veterano";
  if (stage === "legendary") return "Legendario";
  return stage || "Cachorro";
}

function getBarColor(value) {
  const safeValue = Number(value || 0);

  if (safeValue > 65) return "#00e676";
  if (safeValue > 35) return "#ffb300";
  return "#ff1744";
}

function StatBar({ label, value, icon }) {
  const safeValue = Math.max(0, Math.min(100, Number(value || 0)));

  return (
    <Box sx={{ width: "100%", mb: 1.2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 0.4,
        }}
      >
        <Typography sx={{ color: "white", fontSize: 12 }}>
          {icon} {label}
        </Typography>

        <Typography sx={{ color: "#ffcc80", fontSize: 12, fontWeight: 800 }}>
          {Math.round(safeValue)}%
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={safeValue}
        sx={{
          height: 9,
          borderRadius: 10,
          backgroundColor: "rgba(255,255,255,0.16)",
          "& .MuiLinearProgress-bar": {
            borderRadius: 10,
            backgroundColor: getBarColor(safeValue),
          },
        }}
      />
    </Box>
  );
}

function InfoCard({ title, children }) {
  return (
    <Box
      sx={{
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
        border: "1px solid rgba(255,152,0,0.25)",
        borderRadius: 3,
        p: 1.5,
      }}
    >
      <Typography
        sx={{
          color: "#ffb300",
          mb: 1,
          fontWeight: 900,
          fontSize: 14,
          letterSpacing: 0.3,
        }}
      >
        {title}
      </Typography>

      {children}
    </Box>
  );
}

function SmallRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 1,
        mb: 0.5,
      }}
    >
      <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: 13 }}>
        {label}
      </Typography>

      <Typography
        sx={{
          color: "white",
          fontSize: 13,
          fontWeight: 800,
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/* ============================================================
   PET OVERLAY
============================================================ */

export default function PetOverlay() {
  const [petData, setPetData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openPanel, setOpenPanel] = useState(false);
  const [nickname, setNickname] = useState("");
  const [message, setMessage] = useState(null);
  const [animation, setAnimation] = useState("idle");
  const [walkFrame, setWalkFrame] = useState(1);
  const [lastUpdated, setLastUpdated] = useState(null);

  const pet = petData?.pet;
  const cityFarm = petData?.cityFarm;
  const bdb = petData?.bdb;
  const benefit = petData?.benefit;
  const cooldowns = petData?.cooldowns;

  const displayName = useMemo(() => {
    if (!pet) return "My Puppy";
    return pet.nickname || pet.birthName || "My Puppy";
  }, [pet]);

  const waterQty = cityFarm?.inventory?.Water ?? 0;
  const foodQty = cityFarm?.inventory?.Croquettes ?? 0;
  const claimAmount = benefit?.dailyClaim?.miniHueso || 0;

  const refreshPet = async ({ silent = false, keepMessage = true } = {}) => {
    try {
      if (!silent) setLoading(true);

      const res = await petApi().get("/me");

      setPetData(res.data);
      setNickname(res.data?.pet?.nickname || "");
      setLastUpdated(new Date());

      if (!keepMessage) {
        setMessage(null);
      }

      return res.data;
    } catch (err) {
      console.error("[PET FRONT] Error cargando mascota:", err);

      if (!silent) {
        setMessage({
          type: "error",
          text:
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            "No se pudo cargar la mascota.",
        });
      }

      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const updateFromResponseThenRefresh = async (resData) => {
    setPetData((prev) => ({
      ...prev,
      ...resData,
      pet: resData?.pet || prev?.pet,
      benefit: resData?.benefit || prev?.benefit,
      cityFarm: {
        ...(prev?.cityFarm || {}),
        ...(resData?.cityFarm || {}),
      },
      bdb: {
        ...(prev?.bdb || {}),
        ...(resData?.bdb || {}),
      },
    }));

    await refreshPet({
      silent: true,
      keepMessage: true,
    });
  };

  const runAction = async (action, actionAnimation) => {
    try {
      setLoading(true);
      setMessage(null);
      setAnimation(actionAnimation);

      const res = await petApi().post(`/${action}`);

      setMessage({
        type: "success",
        text: res.data?.message || "Acción realizada correctamente.",
      });

      await updateFromResponseThenRefresh(res.data);

      setTimeout(() => {
        setAnimation("idle");
      }, 2500);
    } catch (err) {
      console.error("[PET FRONT] Error acción:", err);

      setAnimation("idle");

      const data = err?.response?.data;

      setMessage({
        type: "error",
        text: data?.cooldown?.text
          ? `${data?.message || "Acción en cooldown."} Espera ${
              data.cooldown.text
            }`
          : data?.message || data?.error || "No se pudo realizar la acción.",
      });

      await refreshPet({
        silent: true,
        keepMessage: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const feedPet = () => runAction("feed", "eat");
  const waterPet = () => runAction("water", "drink");
  const playPet = () => runAction("play", "play");
  const claimDaily = () => runAction("claim-daily", "play");

  const saveNickname = async () => {
    try {
      const cleanNickname = String(nickname || "").trim();

      if (!cleanNickname || cleanNickname.length < 2) {
        setMessage({
          type: "error",
          text: "El sobrenombre debe tener mínimo 2 caracteres.",
        });
        return;
      }

      setLoading(true);
      setMessage(null);

      const res = await petApi().post("/nickname", {
        nickname: cleanNickname,
      });

      setMessage({
        type: "success",
        text: res.data?.message || "Sobrenombre actualizado.",
      });

      await updateFromResponseThenRefresh(res.data);
    } catch (err) {
      console.error("[PET FRONT] Error nickname:", err);

      setMessage({
        type: "error",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "No se pudo cambiar el sobrenombre.",
      });
    } finally {
      setLoading(false);
    }
  };

  const revivePet = async (paymentType) => {
    try {
      setLoading(true);
      setMessage(null);

      const res = await petApi().post("/revive", {
        paymentType,
      });

      setMessage({
        type: "success",
        text: res.data?.message || "Cachorro reactivado correctamente.",
      });

      await updateFromResponseThenRefresh(res.data);
    } catch (err) {
      console.error("[PET FRONT] Error revive:", err);

      setMessage({
        type: "error",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "No se pudo reactivar el cachorro.",
      });

      await refreshPet({
        silent: true,
        keepMessage: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshPet();

    const interval = setInterval(() => {
      refreshPet({
        silent: true,
        keepMessage: true,
      });
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const frameInterval = setInterval(() => {
      setWalkFrame((prev) => {
        if (prev >= 3) return 1;
        return prev + 1;
      });
    }, 350);

    return () => clearInterval(frameInterval);
  }, []);

  if (!pet) return null;

  const floatingImage = getPetImage(
    pet,
    animation === "idle" ? "walk" : animation,
    walkFrame
  );

  const panelImage = getPetImage(pet, animation, walkFrame);
  const canUseActions = pet.status !== "fallen";

  return (
    <>
      {/* ======================================================
          MASCOTA FLOTANTE
      ====================================================== */}
      <Tooltip title="Click para ver tu cachorro" arrow>
        <Box
          onClick={() => setOpenPanel(true)}
          sx={{
            position: "fixed",
            left: { xs: 8, sm: 35 },
            bottom: { xs: 58, sm: 70 },
            zIndex: 9999,
            cursor: "pointer",
            userSelect: "none",
            animation:
              pet.status === "fallen"
                ? "none"
                : animation === "idle"
                ? "petWalk 8s linear infinite alternate"
                : "petBounce 0.6s ease-in-out infinite alternate",
            "@keyframes petWalk": {
              "0%": { transform: "translateX(0px)" },
              "100%": { transform: "translateX(180px)" },
            },
            "@keyframes petBounce": {
              "0%": { transform: "translateY(0px)" },
              "100%": { transform: "translateY(-10px)" },
            },
          }}
        >
          <Box
            sx={{
              background: "rgba(0,0,0,0.78)",
              color: "white",
              border: `1px solid ${
                pet.status === "fallen" ? "#ff1744" : "#ff9800"
              }`,
              borderRadius: "14px",
              px: 1,
              py: 0.5,
              mb: 0.5,
              fontSize: 12,
              textAlign: "center",
              maxWidth: 190,
              boxShadow: "0 0 12px rgba(0,0,0,0.6)",
            }}
          >
            {pet.status === "fallen"
              ? "💀 Necesito ayuda"
              : pet.status === "weak"
              ? "😟 Estoy débil"
              : animation === "eat"
              ? "🍖 Ñam ñam"
              : animation === "drink"
              ? "💧 Tomando agua"
              : animation === "play"
              ? "🎾 Jugando"
              : `🐶 ${displayName}`}
          </Box>

          <Box
            component="img"
            src={floatingImage}
            alt="Big Dog Bone Pet"
            sx={{
              width: { xs: 88, sm: 105 },
              height: { xs: 88, sm: 105 },
              objectFit: "contain",
              filter:
                pet.status === "fallen"
                  ? "grayscale(1) brightness(0.62)"
                  : "drop-shadow(0 0 10px rgba(255,153,0,0.95))",
            }}
          />
        </Box>
      </Tooltip>

      {/* ======================================================
          PANEL
      ====================================================== */}
      <Modal open={openPanel} onClose={() => setOpenPanel(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "94%", sm: 520, md: 680 },
            maxHeight: "92vh",
            overflowY: "auto",
            background:
              "radial-gradient(circle at top, rgba(255,152,0,0.18), transparent 32%), linear-gradient(180deg, #191919, #050505)",
            border: "2px solid #ff9800",
            borderRadius: "22px",
            boxShadow: "0 0 40px rgba(255,152,0,0.45)",
            p: { xs: 2, sm: 3 },
            color: "white",
          }}
        >
          {/* HEADER */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  color: "#ffb300",
                  fontWeight: 900,
                  lineHeight: 1.1,
                }}
              >
                🐶 Big Dog Bone Puppy
              </Typography>

              <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }}>
                Mascota conectada con CityFarm
              </Typography>
            </Box>

            <Chip
              label={statusText(pet.status)}
              sx={{
                color: "black",
                fontWeight: 900,
                backgroundColor: statusColor(pet.status),
              }}
            />
          </Box>

          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          {/* TOP PANEL */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "210px 1fr" },
              gap: 2,
              alignItems: "stretch",
              mb: 2,
            }}
          >
            <Box
              sx={{
                borderRadius: 4,
                p: 2,
                background:
                  "linear-gradient(180deg, rgba(255,152,0,0.12), rgba(255,255,255,0.04))",
                border: "1px solid rgba(255,152,0,0.30)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 180,
              }}
            >
              <Box
                component="img"
                src={panelImage}
                alt="Pet"
                sx={{
                  width: { xs: 150, sm: 170 },
                  height: { xs: 150, sm: 170 },
                  objectFit: "contain",
                  filter:
                    pet.status === "fallen"
                      ? "grayscale(1) brightness(0.62)"
                      : "drop-shadow(0 0 16px rgba(255,153,0,0.85))",
                }}
              />
            </Box>

            <Box>
              <InfoCard title="Identidad">
                <SmallRow label="Nombre de nacimiento" value={pet.birthName} />
                <SmallRow
                  label="Sobrenombre"
                  value={pet.nickname || "Sin sobrenombre"}
                />
                <SmallRow label="Etapa" value={stageText(pet.stage)} />
                <SmallRow label="Estado" value={statusText(pet.status)} />
                <SmallRow label="Nivel" value={pet.level} />
                <SmallRow label="XP" value={pet.xp} />
                <SmallRow label="Edad" value={`${pet.ageDays || 0} días`} />
              </InfoCard>

              <Box sx={{ mt: 1.5 }}>
                <InfoCard title="Saldos Big Dog Bone">
                  <SmallRow
                    label="Mini-Hueso"
                    value={fmtNumber(bdb?.dinero || 0)}
                  />
                  <SmallRow
                    label="HUESO real"
                    value={fmtNumber(bdb?.dineroReal || 0)}
                  />
                  <SmallRow
                    label="Energía"
                    value={
                      bdb?.energia?.actual !== undefined
                        ? `${fmtNumber(bdb?.energia?.actual)} / ${fmtNumber(
                            bdb?.energia?.maximo
                          )}`
                        : "—"
                    }
                  />
                </InfoCard>
              </Box>
            </Box>
          </Box>

          {/* STATS */}
          <InfoCard title="Estado del cachorro">
            <StatBar label="Vida" icon="❤️" value={pet.life?.current || 0} />
            <StatBar label="Hambre" icon="🍖" value={pet.hunger} />
            <StatBar label="Sed" icon="💧" value={pet.thirst} />
            <StatBar label="Felicidad" icon="😊" value={pet.happiness} />
            <StatBar label="Salud" icon="🩺" value={pet.health} />
            <StatBar label="Energía" icon="⚡" value={pet.energy} />
          </InfoCard>

          {/* RESOURCES + BENEFITS */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 1.5,
              mt: 1.5,
            }}
          >
            <InfoCard title="Recursos CityFarm">
              <SmallRow label="💧 Water" value={fmtNumber(waterQty)} />
              <SmallRow label="🍖 Croquettes" value={fmtNumber(foodQty)} />

              <Typography
                sx={{
                  mt: 1,
                  color: "rgba(255,255,255,0.65)",
                  fontSize: 12,
                }}
              >
                Produce estos recursos en CityFarm y úsalos aquí para cuidar tu
                cachorro.
              </Typography>
            </InfoCard>

            <InfoCard title="Beneficio">
              <SmallRow
                label="Estado"
                value={benefit?.active ? "✅ Activo" : "❌ Inactivo"}
              />
              <SmallRow
                label="Reclamo diario"
                value={`${fmtNumber(claimAmount)} mini-hueso`}
              />
              <SmallRow
                label="Bonus mini-hueso"
                value={`${benefit?.bdb?.miniHuesoBonusPercent || 0}%`}
              />
              <SmallRow
                label="Bonus energía"
                value={`${benefit?.bdb?.energyBonusPercent || 0}%`}
              />
            </InfoCard>
          </Box>

          {/* COOLDOWNS */}
          <Box sx={{ mt: 1.5 }}>
            <InfoCard title="Cooldowns">
              <SmallRow
                label="Comida"
                value={cooldowns?.feed?.text || "Disponible"}
              />
              <SmallRow
                label="Agua"
                value={cooldowns?.water?.text || "Disponible"}
              />
              <SmallRow
                label="Jugar"
                value={cooldowns?.play?.text || "Disponible"}
              />
              <SmallRow
                label="Reclamo"
                value={cooldowns?.claim?.text || "Disponible"}
              />
            </InfoCard>
          </Box>

          <Divider sx={{ my: 2, borderColor: "rgba(255,152,0,0.25)" }} />

          {/* ACTIONS */}
          <Stack spacing={1.2}>
            {canUseActions ? (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1,
                  }}
                >
                  <Button
                    disabled={loading}
                    variant="contained"
                    onClick={feedPet}
                    sx={{
                      backgroundColor: "#ff9800",
                      fontWeight: 900,
                      "&:hover": { backgroundColor: "#f57c00" },
                    }}
                  >
                    🍖 Dar Croquettes
                  </Button>

                  <Button
                    disabled={loading}
                    variant="contained"
                    onClick={waterPet}
                    sx={{
                      backgroundColor: "#2196f3",
                      fontWeight: 900,
                      "&:hover": { backgroundColor: "#1976d2" },
                    }}
                  >
                    💧 Dar Water
                  </Button>

                  <Button
                    disabled={loading}
                    variant="contained"
                    onClick={playPet}
                    sx={{
                      backgroundColor: "#4caf50",
                      fontWeight: 900,
                      "&:hover": { backgroundColor: "#388e3c" },
                    }}
                  >
                    🎾 Jugar
                  </Button>

                  <Button
                    disabled={loading}
                    variant="contained"
                    onClick={claimDaily}
                    sx={{
                      backgroundColor: "#9c27b0",
                      fontWeight: 900,
                      "&:hover": { backgroundColor: "#7b1fa2" },
                    }}
                  >
                    🦴 Reclamar diario
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Alert severity="error">
                  Tu cachorro está caído. Puedes reactivarlo usando mini-hueso o
                  HUESO real.
                </Alert>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1,
                  }}
                >
                  <Button
                    disabled={loading}
                    variant="contained"
                    onClick={() => revivePet("dinero")}
                    sx={{
                      backgroundColor: "#ff9800",
                      fontWeight: 900,
                      "&:hover": { backgroundColor: "#f57c00" },
                    }}
                  >
                    Reactivar con mini-hueso
                  </Button>

                  <Button
                    disabled={loading}
                    variant="contained"
                    onClick={() => revivePet("dineroReal")}
                    sx={{
                      backgroundColor: "#f44336",
                      fontWeight: 900,
                      "&:hover": { backgroundColor: "#d32f2f" },
                    }}
                  >
                    Reactivar con HUESO real
                  </Button>
                </Box>
              </>
            )}

            {/* NICKNAME */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mt: 1,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <TextField
                size="small"
                fullWidth
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Sobrenombre del cachorro"
                sx={{
                  input: { color: "white" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#ff9800" },
                    "&:hover fieldset": { borderColor: "#ffb300" },
                    "&.Mui-focused fieldset": { borderColor: "#ffcc80" },
                  },
                }}
              />

              <Button
                disabled={loading}
                variant="outlined"
                onClick={saveNickname}
                sx={{
                  color: "#ffb300",
                  borderColor: "#ffb300",
                  fontWeight: 900,
                  minWidth: { xs: "100%", sm: 110 },
                }}
              >
                Guardar
              </Button>
            </Box>

            {/* FOOTER */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
                mt: 1,
              }}
            >
              <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
                {lastUpdated
                  ? `Actualizado: ${lastUpdated.toLocaleTimeString()}`
                  : "Sin actualizar"}
              </Typography>

              <Button
                disabled={loading}
                variant="text"
                onClick={() =>
                  refreshPet({
                    silent: false,
                    keepMessage: false,
                  })
                }
                sx={{ color: "#ffcc80", fontWeight: 900 }}
              >
                {loading ? "Actualizando..." : "Actualizar todo"}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Modal>
    </>
  );
}